import React, { useMemo, useState, useEffect } from 'react';
import { Box, Typography, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { apiFetch } from '../utils/apiFetch';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import enUS from 'date-fns/locale/en-US';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const locales = { 'en-US': enUS };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});

export default function MoverCalendar({ token }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [view, setView] = useState('month');
  const [date, setDate] = useState(new Date()); // Add date state
  useEffect(() => {
    const fetchCalendar = async () => {
      setLoading(true);
      try {
        const res = await apiFetch('/api/calendar/me');
        const data = await res.json();
        setEvents((data.jobs || []).map(e => ({ ...e, start: new Date(e.start), end: new Date(e.end) })));
      } catch {
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCalendar();
  }, []);
  const handleSelectEvent = event => setSelectedEvent(event);
  const handleClose = () => setSelectedEvent(null);
  // Add event status update handler
  const handleEventAction = async (status) => {
    if (!selectedEvent || !selectedEvent._id) return;
    setActionLoading(true);
    try {
      const res = await apiFetch(`/api/bookings/${selectedEvent._id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error('Failed to update event status');
      // Re-fetch events from backend and update selectedEvent
      const eventsRes = await apiFetch('/api/calendar/me', { headers: { Authorization: `Bearer ${token}` } });
      const data = await eventsRes.json();
      const updatedEvents = (data.jobs || []).map(e => ({ ...e, start: new Date(e.start), end: new Date(e.end) }));
      setEvents(updatedEvents);
      const updated = updatedEvents.find(e => e._id === selectedEvent._id);
      setSelectedEvent(updated || null);
    } catch {
      // Optionally show error feedback
    } finally {
      setActionLoading(false);
    }
  };
  return (
    <Box sx={{ p: { xs: 1, md: 4 }, bgcolor: 'background.default', minHeight: '100vh',
      // Remove blue highlight from selected/today dates in react-big-calendar
      '& .rbc-selected, & .rbc-day-bg.rbc-today': {
        backgroundColor: 'transparent !important',
        boxShadow: 'none',
        outline: 'none',
      },
      '& .rbc-day-bg.rbc-today': {
        backgroundColor: '#f8fafc !important',
      },
    }}>
      <Paper elevation={3} sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 4 }, borderRadius: 4, boxShadow: 4, mb: 4 }}>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 800, color: 'primary.main', letterSpacing: 0.5 }}>Calendar</Typography>
        <Paper sx={{ p: 2, borderRadius: 3, boxShadow: 2, mb: 2, bgcolor: 'background.paper' }}>
          {loading ? <Typography sx={{ textAlign: 'center', py: 6 }}>Loading...</Typography> : (
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              style={{ height: 540, borderRadius: 12, background: '#f8fafc', fontFamily: 'inherit' }}
              onSelectEvent={handleSelectEvent}
              popup
              eventPropGetter={event => ({ style: { backgroundColor: event.status === 'completed' ? '#43a047' : event.status === 'cancelled' ? '#e53935' : '#1976d2', color: '#fff', borderRadius: 6, border: 0, fontWeight: 600, fontSize: 15, boxShadow: '0 2px 8px #0001' } })}
              toolbar={true}
              views={['month', 'week', 'day', 'agenda']}
              view={view}
              onView={setView}
              date={date} // Pass current date
              onNavigate={setDate} // Update date on navigation (including Today)
              messages={{
                today: 'Today',
                previous: '<',
                next: '>',
                month: 'Month',
                week: 'Week',
                day: 'Day',
                agenda: 'Agenda',
              }}
            />
          )}
        </Paper>
        <Dialog open={!!selectedEvent} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
          <DialogTitle sx={{ fontWeight: 700, color: 'primary.main', letterSpacing: 0.5 }}>Event Details</DialogTitle>
          <DialogContent dividers sx={{ bgcolor: 'background.default', p: { xs: 2, sm: 3 } }}>
            {selectedEvent && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography><b>Title:</b> {selectedEvent.title}</Typography>
                <Typography><b>Date:</b> {selectedEvent.start.toLocaleString()}</Typography>
                {selectedEvent.origin && <Typography><b>Origin:</b> {selectedEvent.origin}</Typography>}
                {selectedEvent.destination && <Typography><b>Destination:</b> {selectedEvent.destination}</Typography>}
                {selectedEvent.status && <Typography><b>Status:</b> {selectedEvent.status}</Typography>}
                {selectedEvent.contactName && <Typography><b>Customer:</b> {selectedEvent.contactName}</Typography>}
                {selectedEvent.contactPhone && <Typography><b>Phone:</b> {selectedEvent.contactPhone}</Typography>}
                {selectedEvent.contactEmail && <Typography><b>Email:</b> {selectedEvent.contactEmail}</Typography>}
                {selectedEvent.price && <Typography><b>Price:</b> ${selectedEvent.price}</Typography>}
                {selectedEvent.specialRequirements && <Typography><b>Special Instructions:</b> {selectedEvent.specialRequirements}</Typography>}
                {selectedEvent.truckSize && <Typography><b>Truck Size:</b> {selectedEvent.truckSize}</Typography>}
                {selectedEvent.crewSize && <Typography><b>Crew Size:</b> {selectedEvent.crewSize}</Typography>}
                {selectedEvent.notes && <Typography><b>Notes:</b> {selectedEvent.notes}</Typography>}
                {selectedEvent.createdAt && <Typography><b>Created At:</b> {new Date(selectedEvent.createdAt).toLocaleString()}</Typography>}
                {selectedEvent.updatedAt && <Typography><b>Last Updated:</b> {new Date(selectedEvent.updatedAt).toLocaleString()}</Typography>}
                {selectedEvent._id && <Typography variant="caption" color="text.secondary"><b>Booking ID:</b> {selectedEvent._id}</Typography>}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ bgcolor: 'background.default', p: 2 }}>
            <Button onClick={handleClose} variant="outlined" sx={{ borderRadius: 2 }}>Close</Button>
            {selectedEvent && selectedEvent.status !== 'confirmed' && (
              <Button onClick={() => handleEventAction('confirmed')} disabled={actionLoading} variant="contained" color="primary" sx={{ borderRadius: 2 }}>Confirm</Button>
            )}
            {selectedEvent && selectedEvent.status !== 'completed' && (
              <Button onClick={() => handleEventAction('completed')} disabled={actionLoading} variant="contained" color="success" sx={{ borderRadius: 2 }}>Complete</Button>
            )}
            {selectedEvent && selectedEvent.status !== 'cancelled' && (
              <Button onClick={() => handleEventAction('cancelled')} color="error" disabled={actionLoading} variant="contained" sx={{ borderRadius: 2 }}>Cancel</Button>
            )}
          </DialogActions>
        </Dialog>
      </Paper>
    </Box>
  );
}
